import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    ArrowLeft, Ban, CheckCircle2, ChevronDown, Clock, Eye, FileText, Loader2,
    PackageX, Pill, Plus, RefreshCw, Save, Search, Send, Store as StoreIcon,
    Trash2, Truck, X,
} from 'lucide-react';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import Pagination from '../../components/Pagination';
import CommonLoader from '../../components/CommonLoader';
// Imported under a non-`use` name on purpose — the default export of
// hooks/useAxiosAuth.js is a plain FACTORY that builds an axios instance, not a
// React hook. Binding it as `useAxiosAuth` makes react-hooks/rules-of-hooks flag
// every call site. See the note at the top of hooks/usePermissions.js.
import createAuthedAxios from '../../hooks/useAxiosAuth';
import usePermissions from '../../hooks/usePermissions';
import { extractErrorMessage } from '../../utils/extractErrorMessage';

const MySwal = withReactContent(Swal);

// PURCHASE ORDERS — THE INDENT.
//
// WHY THE REORDER PANEL IS AT THE TOP AND THE REGISTER IS BELOW IT.
// A buyer does not open this screen to read a list of orders they already
// placed; they open it to find out what to order next. The register is the
// receipt of yesterday's work, the suggestions are today's. Putting the table
// first would be filing cabinet before desk — and a reorder list that has to be
// scrolled to is a reorder list that stops being used, which is how a hospital
// runs out of a drug whose reorder level was set correctly two years ago.
//
// GET /purchase-orders/suggest is NOT the shortage list (that is /stock/low). It
// does the three things a shortage list cannot, and each one is why a group here
// can be turned into an order with a single click:
//   - it NETS OFF what is already on order, so the same stock-out is not ordered
//     on Monday by one person and on Wednesday by another;
//   - it GROUPS BY the distributor the product was last received from, because
//     purchasing happens one supplier at a time — one call, one order, one
//     delivery;
//   - it SUGGESTS a quantity that refills past the reorder level rather than back
//     up to it, because a level is a floor and refilling to the floor means
//     raising the same order again tomorrow.
//
// A PURCHASE ORDER MOVES NO STOCK. Nothing on this screen changes a quantity on a
// shelf. Stock comes into being at the goods receipt, when a carton is physically
// on the premises with a batch number and an expiry printed on it — facts an
// order does not have. So nothing here shows or edits a batch, and the "on hand"
// figures below are context for a buying decision, never something this screen
// writes.
//
// TWO REFUSALS THE SERVER MAKES BY NAME, AND THIS SCREEN RESPECTS BOTH:
//   PO_ALREADY_PLACED — a placed order has been SENT. Editing the stored copy
//     would leave the hospital holding a document that disagrees with the one the
//     supplier is picking against, and would erase the evidence that a rate
//     changed. So Edit is only drawn for drafts.
//   PO_HAS_RECEIPTS   — once anything has arrived, a goods receipt points at the
//     order and cancelling it would orphan that receipt. Cancel is only drawn
//     where it can succeed, and the server's refusal is shown verbatim when the
//     screen's copy of the status turns out to be stale.
//
// PERMISSIONS ARE DRAWN, NOT ENFORCED, HERE. The routes are gated by
// requireTenantFeature('pharmacy_inventory') + requirePermission; `can()` below
// only decides whether a button is rendered. A storekeeper who edits the DOM
// still gets a 403.
//
// NO REDUX SLICE, for the same reason MedicineMaster.jsx has none: a slice must
// be registered in src/store.js, and this screen ships before that wiring exists.
// Every call is plain axios through the same authed factory the slices use, so
// lifting them into a purchaseSlice later is a copy rather than a rewrite.

// ---------------------------------------------------------------------------
// Small shared helpers
// ---------------------------------------------------------------------------

const apiError = (err, fallback) => extractErrorMessage(err, fallback);
const errorCode = (err) => err?.response?.data?.code || null;

const toNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

// node-postgres hands DECIMAL back as a STRING so it cannot silently lose
// precision on the wire, and the controller already coerces what it returns. The
// coercion is repeated at the edge anyway because a stored line read back through
// a different route ("120.00" + "80.00" = "120.0080.00") is the one place this
// bites, and it costs nothing to be certain.
const inr = (v) =>
    `₹${toNum(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtQty = (v) => {
    const n = Math.round(toNum(v) * 100) / 100;
    return Number.isInteger(n) ? String(n) : n.toFixed(2);
};

// A DATEONLY column arrives as 'YYYY-MM-DD'. `new Date('2026-04-01')` is parsed
// as UTC midnight, which renders as 31 March for any browser west of Greenwich —
// so an expected delivery date would read a day early and an order would be
// chased a day late. Pinning it to local midnight keeps the printed day the day
// that was keyed.
const fmtDate = (value) => {
    if (!value) return '—';
    const s = String(value);
    const d = /^\d{4}-\d{2}-\d{2}$/.test(s) ? new Date(`${s}T00:00:00`) : new Date(s);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

// The counter's day is India's, and the server pins the same boundary in SQL
// (`NOW() AT TIME ZONE 'Asia/Kolkata'`). A browser in another timezone must not
// decide differently, or an order promised for yesterday reads as still in time
// for the first five and a half hours of every Indian working day — which is the
// window in which somebody decides not to chase it.
const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
const todayIst = () => new Date(Date.now() + IST_OFFSET_MS).toISOString().slice(0, 10);

const daysLate = (expectedDate) => {
    if (!expectedDate) return 0;
    const due = String(expectedDate).slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(due)) return 0;
    const ms = new Date(`${todayIst()}T00:00:00Z`) - new Date(`${due}T00:00:00Z`);
    return Math.max(0, Math.round(ms / 86400000));
};

/* ------------------------------------------------------------------ *
 * PURCHASE MONEY — TAX IS ADDED ON TOP, NOT DIVIDED OUT
 *
 * A mirror of services/pharmacy/purchaseService.computePurchaseLine, and
 * deliberately NOT of the sales-side arithmetic in PharmacyCounter.jsx. On the
 * sales side MRP is tax-INCLUSIVE by law and the tax is extracted from it; on the
 * purchase side the distributor quotes a rate and ADDS GST to it:
 *
 *     gross     = rate * qty
 *     tax       = gross * gstRate / 100
 *     lineTotal = gross + tax
 *
 * Using the inclusive rule here is not a rounding difference, it is a systematic
 * understatement of every line by the tax rate: at 12% a ₹100 purchase books as
 * ₹89.29 of goods and ₹10.71 of tax. The order is what the supplier's invoice is
 * checked against, so the buyer would approve a bill that is 12% larger than the
 * document they raised and never see why.
 *
 * This copy is a PREVIEW ONLY. Nothing computed here is ever posted as an amount
 * — the create and update calls send only medicineId, qty, rate and gstRate, and
 * every stored figure is the server's. The duplication exists because a buyer
 * quotes a total before pressing Save. If the two ever disagree, the server is
 * right and this function is the bug.
 *
 * Counted in paise (money), hundredths (quantity) and basis points (rates), as
 * integers, matching the service. A binary double cannot represent 0.10, so a
 * document assembled in rupees drifts by paise across its lines.
 * ------------------------------------------------------------------ */

// Half AWAY from zero, matching purchaseService.roundHalfAwayFromZero. JavaScript's
// Math.round is half-UP, which is asymmetric about zero; the service documents why
// that asymmetry matters when a return reverses a receipt, and a preview that
// rounds differently from the server would show a total a paisa off the one saved.
const roundHalfAwayFromZero = (n) => (n < 0 ? -Math.round(-n) : Math.round(n));
const scaled2 = (v) => roundHalfAwayFromZero(toNum(v) * 100);
const fromScaled2 = (n) => n / 100;
const mulDiv = (a, b, divisor) => roundHalfAwayFromZero((a * b) / divisor);

function costPurchaseLine({ rate, qty, gstRate }) {
    const rateP = scaled2(rate);
    const qtyU = scaled2(qty);
    const rateBp = scaled2(gstRate);

    // rate and qty are both scaled by 100, so their product is scaled by 10000 and
    // one factor comes back out to leave paise.
    const grossP = mulDiv(rateP, qtyU, 100);
    const taxP = mulDiv(grossP, rateBp, 10000);
    return { grossP, taxP, totalP: grossP + taxP };
}

// The header figures. roundToRupee is FALSE, matching the controller: a purchase
// order has no roundOff column, so rounding the grand total would leave
// subTotal + taxTotal ≠ grandTotal on the face of the order with nowhere to state
// the difference and an approver reasonably asking why the three disagree.
function previewTotals(lines) {
    let subP = 0;
    let taxP = 0;
    lines.forEach((line) => {
        const costed = costPurchaseLine({ rate: line.rate, qty: line.qtyOrdered, gstRate: line.gstRate });
        subP += costed.grossP;
        taxP += costed.taxP;
    });
    return {
        subTotal: fromScaled2(subP),
        taxTotal: fromScaled2(taxP),
        grandTotal: fromScaled2(subP + taxP),
    };
}

// ---------------------------------------------------------------------------
// Presentation constants
// ---------------------------------------------------------------------------

// A draft has NOT been numbered — the serializer sends poNo: null for one,
// because numbering a draft would burn consecutive numbers on orders abandoned
// half-typed. So the screen says "Draft" rather than printing the internal
// placeholder, which somebody would otherwise read down a telephone.
const STATUS_TONE = {
    draft: 'bg-slate-100 text-slate-600 border-slate-300',
    placed: 'bg-[#4ABDBD]/10 text-[#2D8A8A] border-[#4ABDBD]/50',
    partial: 'bg-amber-50 text-amber-700 border-amber-300',
    received: 'bg-emerald-50 text-emerald-700 border-emerald-300',
    cancelled: 'bg-rose-50 text-rose-600 border-rose-300',
};

// The register's tabs. 'open' is the CHASE LIST — everything sent and not yet
// fully delivered — and it is the view the screen opens on, because an order
// nobody chases is an order the distributor forgets. The backend offers it as a
// single `open=true` flag precisely so a caller cannot forget one of its two
// statuses.
const REGISTER_TABS = [
    { id: 'open', labelKey: 'purchaseorders.tab_open', label: 'Awaiting delivery', params: { open: 'true' } },
    { id: 'draft', labelKey: 'purchaseorders.tab_draft', label: 'Drafts', params: { status: 'draft' } },
    { id: 'received', labelKey: 'purchaseorders.tab_received', label: 'Completed', params: { status: 'received' } },
    { id: 'cancelled', labelKey: 'purchaseorders.tab_cancelled', label: 'Cancelled', params: { status: 'cancelled' } },
    { id: 'all', labelKey: 'purchaseorders.tab_all', label: 'All', params: {} },
];

const STORE_STORAGE_KEY = 'pharmacyPurchaseStoreId';

// How many times the reorder level to refill to. The server clamps to 1..6 and
// defaults to 2; the same options are offered here so the buyer never sends a
// value that comes back silently adjusted.
const COVER_OPTIONS = [1, 1.5, 2, 3, 4];

// A supplier group with more items than this is collapsed to a preview. Forty
// lines of table before the next supplier's name is how the second call of the
// morning never gets made.
const GROUP_PREVIEW_ROWS = 5;

// Line keys for React. Two lines can legitimately hold the same medicine (two
// pack sizes on one order), so the medicineId is not a key — a monotonic counter
// is, and it survives re-ordering and deletion without React reusing an input's
// DOM node and its cursor position along with it.
let lineSeq = 0;
const nextLineKey = () => `ln${(lineSeq += 1)}`;

// ---------------------------------------------------------------------------
// Type-ahead
// ---------------------------------------------------------------------------

/**
 * A debounced lookup shared by the supplier and medicine pickers.
 *
 * `fetcher` MUST be stable (wrap it in useCallback) or the effect re-runs on
 * every keystroke's render and the debounce never expires. Requests are
 * cancelled on the way out rather than merely ignored, so a slow answer to
 * "amo" cannot land after a fast answer to "amoxicillin" and repaint the list
 * with the wrong rows under a cursor that has moved on.
 */
function useTypeahead(fetcher, { minChars = 2, delay = 300 } = {}) {
    const [term, setTerm] = useState('');
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const q = term.trim();
        if (q.length < minChars) {
            setRows([]);
            setLoading(false);
            return undefined;
        }
        let cancelled = false;
        setLoading(true);
        const timer = setTimeout(async () => {
            try {
                const found = await fetcher(q);
                if (!cancelled) setRows(Array.isArray(found) ? found : []);
            } catch {
                // A failed type-ahead never raises a banner: the user is
                // mid-keystroke, the next one supersedes it, and a toast per
                // character trains people to dismiss the real ones unread.
                if (!cancelled) setRows([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }, delay);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [term, fetcher, minChars, delay]);

    return { term, setTerm, rows, loading, reset: () => { setTerm(''); setRows([]); } };
}

/**
 * The distributor picker.
 *
 * Backed by /suppliers/search rather than the list endpoint, which is the same
 * split the backend made deliberately: the search skips the payables aggregates,
 * so a dropdown does not cost three grouped scans per keystroke. It also returns
 * ACTIVE suppliers only — the whole point of deactivating a party (a lapsed drug
 * licence, an open dispute) is that no new order can be raised on it, and a
 * picker that still offered one would make the deactivation decorative.
 */
function SupplierPicker({ axiosInstance, value, onChange, disabled, t }) {
    const [open, setOpen] = useState(false);
    const boxRef = useRef(null);

    const fetcher = useCallback(
        async (q) => {
            const res = await axiosInstance.get('/pharmacy/suppliers/search', { params: { q, limit: 20 } });
            return res?.data?.data || [];
        },
        [axiosInstance]
    );
    const { term, setTerm, rows, loading, reset } = useTypeahead(fetcher);

    useEffect(() => {
        const onDocClick = (e) => {
            if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, []);

    if (value?.supplierId) {
        return (
            <div className="flex items-center gap-2 flex-wrap">
                <span
                    data-testid="po-supplier-chosen"
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-[#4ABDBD]/10 border border-[#4ABDBD]/40 text-sm font-semibold text-[#2D8A8A]"
                >
                    <Truck className="w-4 h-4" />
                    {value.name}
                    {value.paymentTermDays !== null && value.paymentTermDays !== undefined && (
                        <span className="text-[11px] font-normal text-slate-500">
                            {t('purchaseorders.terms_days', '{{days}} day terms', { days: value.paymentTermDays })}
                        </span>
                    )}
                </span>
                {!disabled && (
                    <button
                        type="button"
                        data-testid="po-supplier-clear"
                        onClick={() => { onChange(null); reset(); setOpen(true); }}
                        className="text-slate-400 hover:text-rose-500 cursor-pointer"
                        title={t('purchaseorders.change_supplier', 'Choose a different distributor')}
                    >
                        <X className="w-4 h-4" />
                    </button>
                )}
            </div>
        );
    }

    return (
        <div ref={boxRef} className="relative w-full sm:w-80">
            <input
                type="text"
                data-testid="po-supplier-search"
                disabled={disabled}
                value={term}
                onChange={(e) => { setTerm(e.target.value.replace(/[<>]/g, '').slice(0, 60)); setOpen(true); }}
                onFocus={() => setOpen(true)}
                placeholder={t('purchaseorders.supplier_placeholder', 'Search distributor by name, contact, phone or GSTIN…')}
                className="w-full p-2 pl-9 border border-amber-300 bg-amber-50/40 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
            />
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4ABDBD]" />
            {open && term.trim().length >= 2 && (
                <div className="absolute z-30 mt-1 w-full max-h-64 overflow-y-auto bg-white border border-gray-200 rounded-xl shadow-xl">
                    {loading && (
                        <div className="p-3 text-xs text-slate-400 flex items-center gap-2">
                            <Loader2 className="w-3 h-3 animate-spin" />
                            {t('purchaseorders.searching', 'Searching…')}
                        </div>
                    )}
                    {!loading && rows.length === 0 && (
                        <div className="p-3 text-xs text-slate-400">
                            {t('purchaseorders.no_supplier_match', 'No active distributor matches. Add them under Suppliers first.')}
                        </div>
                    )}
                    {rows.map((s) => (
                        <button
                            key={s.supplierId}
                            type="button"
                            data-testid={`po-supplier-option-${s.supplierId}`}
                            onClick={() => { onChange(s); reset(); setOpen(false); }}
                            className="w-full text-left px-3 py-2 hover:bg-[#4ABDBD]/10 cursor-pointer border-b border-gray-50 last:border-0"
                        >
                            <div className="text-sm font-semibold text-gray-700">{s.name}</div>
                            <div className="text-[11px] text-slate-400">
                                {[s.contactPerson, s.phone, s.gstin].filter(Boolean).join(' · ') || '—'}
                            </div>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

/**
 * Adding a line by hand.
 *
 * Searches the drug master for the chosen counter, so the row already carries the
 * GST slab and the pack size — a rate is meaningless without the pack (₹84 is a
 * good rate or a bad one depending on whether the pack holds 10 tablets or 15),
 * and the slab is snapshotted onto the order so a later revision shows up as a
 * difference to argue about rather than silently rewriting what was agreed.
 */
function MedicineLinePicker({ axiosInstance, storeId, onAdd, disabled, t }) {
    const [open, setOpen] = useState(false);
    const boxRef = useRef(null);

    const fetcher = useCallback(
        async (q) => {
            const res = await axiosInstance.get('/pharmacy/medicines/search', {
                params: { q, storeId: storeId || undefined, limit: 15 },
            });
            return res?.data?.data || [];
        },
        [axiosInstance, storeId]
    );
    const { term, setTerm, rows, loading, reset } = useTypeahead(fetcher);

    useEffect(() => {
        const onDocClick = (e) => {
            if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, []);

    return (
        <div ref={boxRef} className="relative w-full sm:w-96">
            <input
                type="text"
                data-testid="po-medicine-search"
                disabled={disabled}
                value={term}
                onChange={(e) => { setTerm(e.target.value.replace(/[<>]/g, '').slice(0, 60)); setOpen(true); }}
                onFocus={() => setOpen(true)}
                placeholder={t('purchaseorders.add_line_placeholder', 'Add a product — search name, salt or brand…')}
                className="w-full p-2 pl-9 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
            />
            <Plus className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4ABDBD]" />
            {open && term.trim().length >= 2 && (
                <div className="absolute z-30 mt-1 w-full max-h-72 overflow-y-auto bg-white border border-gray-200 rounded-xl shadow-xl">
                    {loading && (
                        <div className="p-3 text-xs text-slate-400 flex items-center gap-2">
                            <Loader2 className="w-3 h-3 animate-spin" />
                            {t('purchaseorders.searching', 'Searching…')}
                        </div>
                    )}
                    {!loading && rows.length === 0 && (
                        <div className="p-3 text-xs text-slate-400">
                            {t('purchaseorders.no_medicine_match', 'Nothing in the drug master matches. Catalogue it first.')}
                        </div>
                    )}
                    {rows.map((m) => (
                        <button
                            key={m.medicineId}
                            type="button"
                            data-testid={`po-medicine-option-${m.medicineId}`}
                            onClick={() => { onAdd(m); reset(); setOpen(false); }}
                            className="w-full text-left px-3 py-2 hover:bg-[#4ABDBD]/10 cursor-pointer border-b border-gray-50 last:border-0"
                        >
                            <div className="flex items-center justify-between gap-2">
                                <span className="text-sm font-semibold text-gray-700">{m.name}</span>
                                <span className="text-[11px] text-slate-400 whitespace-nowrap">
                                    {t('purchaseorders.gst_at', 'GST {{rate}}%', { rate: toNum(m.gstRate) })}
                                </span>
                            </div>
                            <div className="text-[11px] text-slate-400">
                                {[m.brand, m.strength, m.packSize].filter(Boolean).join(' · ') || m.genericName || '—'}
                                {' · '}
                                {t('purchaseorders.on_hand_short', 'on hand {{qty}}', { qty: fmtQty(m.qtyOnHand) })}
                            </div>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// The reorder suggestions — one supplier's worth
// ---------------------------------------------------------------------------

function SuggestionGroup({ group, expanded, onToggle, onDraft, canManage, busy, t }) {
    const unknownSupplier = !group.supplierId;
    const supplierInactive = !!group.supplierId && group.supplierStatus && group.supplierStatus !== 'active';
    // A rate we have never paid cannot be guessed. The order is what the
    // supplier's invoice is later checked against, so a line at zero would pass
    // any bill they send — the buyer has to key it, and the one-click path is
    // withheld rather than quietly producing a document with no prices on it.
    const missingRates = group.items.filter((i) => i.lastPurchaseRate === null || i.lastPurchaseRate === undefined).length;
    const needsReview = unknownSupplier || missingRates > 0;
    const rows = expanded ? group.items : group.items.slice(0, GROUP_PREVIEW_ROWS);
    const hidden = group.items.length - rows.length;

    return (
        <div
            data-testid={`po-suggest-group-${group.supplierId || 'unassigned'}`}
            className={`rounded-2xl border shadow-sm overflow-hidden ${unknownSupplier ? 'border-amber-300 bg-amber-50/30' : 'border-gray-200 bg-white'}`}
        >
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 p-4 border-b border-gray-100">
                <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <Truck className={`w-4 h-4 ${unknownSupplier ? 'text-amber-500' : 'text-[#2D8A8A]'}`} />
                        <span className="font-bold text-gray-800">
                            {group.supplierName || t('purchaseorders.no_supplier_group', 'Never bought before')}
                        </span>
                        {group.paymentTermDays !== null && group.paymentTermDays !== undefined && (
                            <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-600 text-[11px] font-semibold">
                                {t('purchaseorders.terms_days', '{{days}} day terms', { days: group.paymentTermDays })}
                            </span>
                        )}
                        {supplierInactive && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-rose-50 text-rose-600 border border-rose-200 text-[11px] font-bold">
                                <Ban className="w-3 h-3" />
                                {t('purchaseorders.supplier_inactive', 'Deactivated — the order will be refused')}
                            </span>
                        )}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                        {unknownSupplier
                            ? t('purchaseorders.no_supplier_hint', 'These have never been received from anybody. Someone has to decide the distributor — that is why they are listed rather than dropped.')
                            : t('purchaseorders.group_summary', '{{count}} product(s) · about {{value}} before tax, at the rate last paid', {
                                count: group.itemCount,
                                value: inr(group.estimatedValue),
                            })}
                    </div>
                </div>

                <button
                    type="button"
                    data-testid={`po-draft-group-${group.supplierId || 'unassigned'}`}
                    disabled={!canManage || busy}
                    onClick={() => onDraft(group, needsReview)}
                    title={canManage
                        ? undefined
                        : t('purchaseorders.no_permission', 'Raising orders needs the purchasing permission')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold whitespace-nowrap shadow-md transition-all duration-300 ${canManage && !busy
                        ? 'bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white cursor-pointer hover:shadow-xl hover:scale-105'
                        : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}
                >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                    {needsReview
                        ? t('purchaseorders.review_and_draft', 'Review & draft')
                        : t('purchaseorders.draft_this_order', 'Draft this order')}
                </button>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full text-xs">
                    <thead>
                        <tr className="bg-slate-50 text-slate-500">
                            <th className="p-2 text-left font-semibold">{t('purchaseorders.product', 'Product')}</th>
                            <th className="p-2 text-right font-semibold">{t('purchaseorders.on_hand', 'On hand')}</th>
                            <th className="p-2 text-right font-semibold">{t('purchaseorders.level', 'Level')}</th>
                            <th className="p-2 text-right font-semibold">{t('purchaseorders.on_order', 'On order')}</th>
                            <th className="p-2 text-right font-semibold">{t('purchaseorders.suggested', 'Suggest')}</th>
                            <th className="p-2 text-right font-semibold">{t('purchaseorders.last_rate', 'Last rate')}</th>
                            <th className="p-2 text-right font-semibold">{t('purchaseorders.estimate', 'Estimate')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((item) => (
                            <tr key={item.medicineId} className="border-b border-gray-50 last:border-0">
                                <td className="p-2">
                                    <div className="flex items-center gap-2">
                                        {item.outOfStock
                                            ? <PackageX className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                                            : <Pill className="w-3.5 h-3.5 text-[#2D8A8A] flex-shrink-0" />}
                                        <div className="min-w-0">
                                            <div className="font-semibold text-gray-700 truncate">{item.name}</div>
                                            <div className="text-[10px] text-slate-400 truncate">
                                                {[item.brand, item.strength, item.packSize].filter(Boolean).join(' · ')
                                                    || item.genericName || ''}
                                            </div>
                                        </div>
                                    </div>
                                </td>
                                {/* Zero on hand is the loudest number on this table: it is a
                                    product a patient is being turned away from today, not one
                                    that merely dipped below its buffer. */}
                                <td className={`p-2 text-right font-bold ${item.outOfStock ? 'text-red-600' : 'text-gray-700'}`}>
                                    {fmtQty(item.qtyOnHand)}
                                </td>
                                <td className="p-2 text-right text-slate-500">{fmtQty(item.reorderLevel)}</td>
                                <td className="p-2 text-right text-slate-500">
                                    {item.qtyOnOrder > 0 ? fmtQty(item.qtyOnOrder) : '—'}
                                </td>
                                <td className="p-2 text-right font-bold text-[#2D8A8A]">{fmtQty(item.suggestedQty)}</td>
                                <td className="p-2 text-right text-slate-600">
                                    {item.lastPurchaseRate === null || item.lastPurchaseRate === undefined ? (
                                        <span className="text-amber-600 font-semibold" title={t('purchaseorders.rate_unknown_hint', 'Never received — nobody has paid a rate for this yet')}>
                                            {t('purchaseorders.rate_unknown', 'not known')}
                                        </span>
                                    ) : inr(item.lastPurchaseRate)}
                                </td>
                                <td className="p-2 text-right text-slate-600">
                                    {item.estimatedAmount > 0 ? inr(item.estimatedAmount) : '—'}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {(hidden > 0 || expanded) && group.items.length > GROUP_PREVIEW_ROWS && (
                <button
                    type="button"
                    data-testid={`po-suggest-toggle-${group.supplierId || 'unassigned'}`}
                    onClick={onToggle}
                    className="w-full flex items-center justify-center gap-1 py-2 text-xs font-semibold text-[#2D8A8A] hover:bg-[#4ABDBD]/5 cursor-pointer"
                >
                    {expanded
                        ? <>{t('purchaseorders.show_less', 'Show fewer')} <ChevronDown className="w-3 h-3 rotate-180" /></>
                        : <>{t('purchaseorders.show_more', 'Show {{n}} more', { n: hidden })} <ChevronDown className="w-3 h-3" /></>}
                </button>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// The draft editor
// ---------------------------------------------------------------------------

/**
 * Build, save and place one order.
 *
 * Defined at module level rather than inside the page component on purpose: a
 * component declared inside a render is a NEW component type on every render, so
 * React unmounts and remounts the whole subtree and every input loses focus and
 * its cursor position on each keystroke. That is invisible in a read-only card
 * and unusable in a form.
 *
 * SAVE IS ALWAYS A DRAFT. Placing is a separate, deliberate act because it is the
 * moment the order becomes a promise the hospital has made — it allocates the
 * consecutive number and is what the supplier picks against. The two-button
 * ending ("Save draft" / "Save & place") keeps that distinction in front of the
 * person making it.
 */
function DraftEditor({
    axiosInstance, draft, setDraft, stores, canManage, saving, onSave, onCancel, t,
}) {
    // Memoised on `draft.lines` rather than on a `draft.lines || []` expression:
    // the fallback builds a fresh array on every render, which would make the
    // totals below recompute on each keystroke anywhere on the form.
    const lines = useMemo(() => draft.lines || [], [draft.lines]);
    const totals = useMemo(() => previewTotals(lines), [lines]);
    const isSaved = !!draft.poId;

    const patchLine = (key, patch) =>
        setDraft((d) => ({ ...d, lines: d.lines.map((l) => (l.key === key ? { ...l, ...patch } : l)) }));

    const removeLine = (key) =>
        setDraft((d) => ({ ...d, lines: d.lines.filter((l) => l.key !== key) }));

    const addLine = (medicine) => {
        setDraft((d) => {
            // A product already on the order is not silently duplicated — two lines
            // for one medicine is legitimate (two pack sizes) but almost always a
            // double-click, and a distributor shipping both is a delivery nobody
            // ordered.
            if (d.lines.some((l) => l.medicineId === medicine.medicineId)) {
                toast.info(t('purchaseorders.already_on_order', '{{name}} is already on this order', { name: medicine.name }));
                return d;
            }
            return {
                ...d,
                lines: [
                    ...d.lines,
                    {
                        key: nextLineKey(),
                        medicineId: medicine.medicineId,
                        medicineName: medicine.name,
                        packSize: medicine.packSize || '',
                        unit: medicine.unit || '',
                        gstRate: toNum(medicine.gstRate),
                        qtyOrdered: '',
                        rate: '',
                        hint: {
                            qtyOnHand: toNum(medicine.qtyOnHand),
                            reorderLevel: toNum(medicine.reorderLevel),
                            qtyOnOrder: null,
                        },
                    },
                ],
            };
        });
    };

    const zeroRateLines = lines.filter((l) => toNum(l.rate) === 0).length;
    const badQtyLines = lines.filter((l) => !(toNum(l.qtyOrdered) > 0)).length;
    const blocked = !draft.supplierId || lines.length === 0 || badQtyLines > 0;

    return (
        <div data-testid="po-draft-editor" className="space-y-4">
            <div className="flex items-center gap-3">
                <button
                    type="button"
                    data-testid="po-draft-back"
                    onClick={onCancel}
                    className="flex items-center gap-1 text-sm text-slate-500 hover:text-[#2D8A8A] cursor-pointer"
                >
                    <ArrowLeft className="w-4 h-4" />
                    {t('purchaseorders.back_to_list', 'Back to purchase orders')}
                </button>
                {isSaved && (
                    <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-300 text-[11px] font-bold">
                        {draft.poNo || t('purchaseorders.draft', 'Draft')}
                    </span>
                )}
            </div>

            <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                <h2 className="text-lg font-bold text-gray-800 mb-1">
                    {isSaved
                        ? t('purchaseorders.edit_draft_title', 'Edit draft order')
                        : t('purchaseorders.new_order_title', 'New purchase order')}
                </h2>
                {/* Said in words, once, where the decision is made. A buyer who
                    believes saving this puts stock on the shelf will not chase the
                    delivery, and the counter finds out from a patient. */}
                <p className="text-xs text-slate-400 mb-4">
                    {t('purchaseorders.draft_subtitle', 'An order moves no stock. Nothing reaches a shelf until the delivery is received against it.')}
                </p>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-xs font-semibold text-slate-500 mb-1">
                            {t('purchaseorders.supplier', 'Distributor')}
                        </label>
                        <SupplierPicker
                            axiosInstance={axiosInstance}
                            value={draft.supplierId ? { supplierId: draft.supplierId, name: draft.supplierName, paymentTermDays: draft.paymentTermDays } : null}
                            onChange={(s) => setDraft((d) => ({
                                ...d,
                                supplierId: s?.supplierId || null,
                                supplierName: s?.name || null,
                                paymentTermDays: s?.paymentTermDays ?? null,
                            }))}
                            disabled={!canManage || saving}
                            t={t}
                        />
                        {!draft.supplierId && (
                            <p className="text-[11px] text-amber-600 mt-1">
                                {t('purchaseorders.supplier_required', 'An order with no party is a wish list.')}
                            </p>
                        )}
                    </div>

                    <div>
                        <label className="block text-xs font-semibold text-slate-500 mb-1">
                            {t('purchaseorders.deliver_to', 'Deliver into')}
                        </label>
                        <select
                            data-testid="po-draft-store"
                            value={draft.storeId || ''}
                            disabled={!canManage || saving}
                            onChange={(e) => setDraft((d) => ({ ...d, storeId: e.target.value }))}
                            className="tt-select w-full p-2 border border-gray-300 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                        >
                            {stores.map((s) => (
                                <option key={s.storeId} value={s.storeId}>
                                    {s.name}{s.isDefault ? ` · ${t('purchaseorders.default_store', 'default')}` : ''}
                                </option>
                            ))}
                        </select>
                        {/* Stock is held per counter, so an order is raised into ONE
                            of them. Receiving a ward's consignment into the front desk
                            is a stock count that never reconciles. */}
                        <p className="text-[11px] text-slate-400 mt-1">
                            {t('purchaseorders.store_hint', 'Stock is held per counter — the goods receipt lands here.')}
                        </p>
                    </div>

                    <div>
                        <label className="block text-xs font-semibold text-slate-500 mb-1">
                            {t('purchaseorders.expected_date', 'Promised for')}
                        </label>
                        <input
                            type="date"
                            data-testid="po-draft-expected"
                            value={draft.expectedDate || ''}
                            min={todayIst()}
                            disabled={!canManage || saving}
                            onChange={(e) => setDraft((d) => ({ ...d, expectedDate: e.target.value }))}
                            className="w-full p-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                        />
                        <p className="text-[11px] text-slate-400 mt-1">
                            {t('purchaseorders.expected_hint', 'A promise with a date is one you can chase.')}
                        </p>
                    </div>
                </div>

                <div className="mt-4">
                    <label className="block text-xs font-semibold text-slate-500 mb-1">
                        {t('purchaseorders.notes', 'Notes to the distributor')}
                    </label>
                    <textarea
                        data-testid="po-draft-notes"
                        rows={2}
                        maxLength={500}
                        value={draft.notes || ''}
                        disabled={!canManage || saving}
                        onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value.slice(0, 500) }))}
                        className="w-full p-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                        placeholder={t('purchaseorders.notes_placeholder', 'Delivery window, cold-chain instructions, reference to a quotation…')}
                    />
                </div>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
                    <h3 className="font-bold text-gray-800">
                        {t('purchaseorders.lines', 'What we are asking for')}
                        <span className="ml-2 text-xs font-normal text-slate-400">
                            {t('purchaseorders.line_count', '{{n}} line(s)', { n: lines.length })}
                        </span>
                    </h3>
                    <MedicineLinePicker
                        axiosInstance={axiosInstance}
                        storeId={draft.storeId}
                        onAdd={addLine}
                        disabled={!canManage || saving}
                        t={t}
                    />
                </div>

                <div className="overflow-x-auto rounded-xl border border-gray-200">
                    <table data-testid="po-draft-lines" className="w-full text-xs">
                        <thead>
                            <tr className="bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white">
                                <th className="p-2 text-left font-bold uppercase tracking-wider">{t('purchaseorders.product', 'Product')}</th>
                                <th className="p-2 text-right font-bold uppercase tracking-wider">{t('purchaseorders.qty', 'Qty')}</th>
                                <th className="p-2 text-right font-bold uppercase tracking-wider">{t('purchaseorders.rate_ex_tax', 'Rate (ex tax)')}</th>
                                <th className="p-2 text-right font-bold uppercase tracking-wider">{t('purchaseorders.gst', 'GST %')}</th>
                                <th className="p-2 text-right font-bold uppercase tracking-wider">{t('purchaseorders.taxable', 'Taxable')}</th>
                                <th className="p-2 text-right font-bold uppercase tracking-wider">{t('purchaseorders.tax', 'Tax')}</th>
                                <th className="p-2 text-right font-bold uppercase tracking-wider">{t('purchaseorders.line_total', 'Line total')}</th>
                                <th className="p-2" />
                            </tr>
                        </thead>
                        <tbody>
                            {lines.length === 0 && (
                                <tr>
                                    <td colSpan={8} className="p-6 text-center text-slate-400">
                                        {t('purchaseorders.no_lines', 'Nothing on this order yet — search a product above to add the first line.')}
                                    </td>
                                </tr>
                            )}
                            {lines.map((line) => {
                                const costed = costPurchaseLine({ rate: line.rate, qty: line.qtyOrdered, gstRate: line.gstRate });
                                const qtyBad = !(toNum(line.qtyOrdered) > 0);
                                return (
                                    <tr key={line.key} data-testid={`po-draft-line-${line.medicineId}`} className="border-b border-gray-100 last:border-0">
                                        <td className="p-2">
                                            <div className="font-semibold text-gray-700">{line.medicineName}</div>
                                            <div className="text-[10px] text-slate-400">
                                                {line.packSize
                                                    ? t('purchaseorders.pack_of', 'pack of {{pack}}', { pack: line.packSize })
                                                    : t('purchaseorders.no_pack', 'pack size not recorded')}
                                                {line.hint && line.hint.reorderLevel > 0 && (
                                                    <> · {t('purchaseorders.hint_stock', 'holding {{onHand}}, level {{level}}', {
                                                        onHand: fmtQty(line.hint.qtyOnHand),
                                                        level: fmtQty(line.hint.reorderLevel),
                                                    })}</>
                                                )}
                                            </div>
                                        </td>
                                        <td className="p-2 text-right">
                                            <input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                data-testid={`po-line-qty-${line.medicineId}`}
                                                value={line.qtyOrdered}
                                                disabled={!canManage || saving}
                                                onChange={(e) => patchLine(line.key, { qtyOrdered: e.target.value })}
                                                className={`w-24 p-1.5 text-right border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] ${qtyBad ? 'border-rose-300 bg-rose-50' : 'border-gray-300'}`}
                                            />
                                        </td>
                                        <td className="p-2 text-right">
                                            <input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                data-testid={`po-line-rate-${line.medicineId}`}
                                                value={line.rate}
                                                disabled={!canManage || saving}
                                                onChange={(e) => patchLine(line.key, { rate: e.target.value })}
                                                className="w-28 p-1.5 text-right border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                                            />
                                        </td>
                                        <td className="p-2 text-right">
                                            <input
                                                type="number"
                                                min="0"
                                                max="100"
                                                step="0.01"
                                                data-testid={`po-line-gst-${line.medicineId}`}
                                                value={line.gstRate}
                                                disabled={!canManage || saving}
                                                onChange={(e) => patchLine(line.key, { gstRate: e.target.value })}
                                                className="w-20 p-1.5 text-right border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                                            />
                                        </td>
                                        <td className="p-2 text-right text-slate-600">{inr(fromScaled2(costed.grossP))}</td>
                                        <td className="p-2 text-right text-slate-500">{inr(fromScaled2(costed.taxP))}</td>
                                        <td className="p-2 text-right font-bold text-gray-700">{inr(fromScaled2(costed.totalP))}</td>
                                        <td className="p-2 text-right">
                                            <button
                                                type="button"
                                                data-testid={`po-line-remove-${line.medicineId}`}
                                                disabled={!canManage || saving}
                                                onClick={() => removeLine(line.key)}
                                                className="text-rose-400 hover:text-rose-600 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                                                title={t('purchaseorders.remove_line', 'Remove this line')}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* THE PREVIEW SAYS SO. Every figure below is computed in the browser
                    with the same exclusive-GST rule the server uses; what is stored is
                    always the server's answer. Labelling it is cheaper than a buyer
                    wondering which of two totals is the real one. */}
                <div className="mt-4 flex flex-col lg:flex-row justify-between gap-4">
                    <div className="text-[11px] text-slate-400 max-w-xl space-y-1">
                        <p>
                            {t('purchaseorders.tax_note', 'The distributor quotes a rate and adds GST on top — unlike a sale, where MRP already includes it. These totals are a preview; the saved order carries the server’s figures.')}
                        </p>
                        {zeroRateLines > 0 && (
                            <p className="text-amber-600 font-semibold" data-testid="po-zero-rate-warning">
                                {t('purchaseorders.zero_rate_warning', '{{n}} line(s) have no rate. The order is what the supplier’s invoice is checked against — a line at zero will accept any price they bill.', { n: zeroRateLines })}
                            </p>
                        )}
                        {badQtyLines > 0 && (
                            <p className="text-rose-600 font-semibold" data-testid="po-bad-qty-warning">
                                {t('purchaseorders.bad_qty_warning', '{{n}} line(s) have no quantity.', { n: badQtyLines })}
                            </p>
                        )}
                    </div>

                    <div className="w-full lg:w-72 rounded-xl bg-slate-50 border border-slate-200 p-3 text-sm">
                        <div className="flex justify-between text-slate-600">
                            <span>{t('purchaseorders.sub_total', 'Goods value')}</span>
                            <span data-testid="po-preview-subtotal">{inr(totals.subTotal)}</span>
                        </div>
                        <div className="flex justify-between text-slate-600 mt-1">
                            <span>{t('purchaseorders.tax_total', 'GST')}</span>
                            <span data-testid="po-preview-tax">{inr(totals.taxTotal)}</span>
                        </div>
                        <div className="flex justify-between font-bold text-gray-800 mt-2 pt-2 border-t border-slate-200">
                            <span>{t('purchaseorders.grand_total', 'Order value')}</span>
                            <span data-testid="po-preview-total">{inr(totals.grandTotal)}</span>
                        </div>
                    </div>
                </div>

                <div className="mt-4 flex flex-col sm:flex-row gap-2 justify-end">
                    <button
                        type="button"
                        data-testid="po-save-draft"
                        disabled={!canManage || saving || blocked}
                        onClick={() => onSave({ place: false })}
                        className={`flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border transition-all duration-300 ${canManage && !saving && !blocked
                            ? 'border-[#4ABDBD] text-[#2D8A8A] hover:bg-[#4ABDBD]/10 cursor-pointer'
                            : 'border-slate-200 text-slate-400 cursor-not-allowed'}`}
                    >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        {t('purchaseorders.save_draft', 'Save draft')}
                    </button>
                    <button
                        type="button"
                        data-testid="po-save-and-place"
                        disabled={!canManage || saving || blocked}
                        onClick={() => onSave({ place: true })}
                        className={`flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold shadow-md transition-all duration-300 ${canManage && !saving && !blocked
                            ? 'bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white cursor-pointer hover:shadow-xl hover:scale-105'
                            : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}
                    >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        {t('purchaseorders.save_and_place', 'Save & place')}
                    </button>
                </div>
                <p className="mt-2 text-[11px] text-slate-400 text-right">
                    {t('purchaseorders.place_hint', 'Placing takes the next order number and freezes the lines — after that a change means cancelling and raising another.')}
                </p>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// One order, opened
// ---------------------------------------------------------------------------

function OrderDetail({ order, loading, onClose, onPlace, onCancelOrder, onEdit, canManage, busy, t }) {
    if (!order && !loading) return null;
    const items = order?.items || [];
    const receipts = order?.receipts || [];
    const late = order && ['placed', 'partial'].includes(order.status) ? daysLate(order.expectedDate) : 0;

    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 overflow-y-auto">
            <div data-testid="po-detail-modal" className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl my-8">
                <div className="flex items-start justify-between gap-3 p-4 border-b border-gray-100">
                    <div>
                        {/* While the order is still loading the heading says so
                            rather than falling through to "Draft" — a placed order
                            that reads "Draft" for half a second is the one word on
                            this panel somebody might act on. */}
                        <h2 className="text-lg font-bold text-gray-800">
                            {loading || !order
                                ? t('purchaseorders.loading', 'Loading...')
                                : (order.poNo || t('purchaseorders.draft', 'Draft'))}
                        </h2>
                        <p className="text-xs text-slate-400">
                            {order && t('purchaseorders.raised_on', 'Raised {{date}}', { date: fmtDate(order.poDate) })}
                        </p>
                    </div>
                    <button
                        type="button"
                        data-testid="po-detail-close"
                        onClick={onClose}
                        className="text-slate-400 hover:text-slate-700 cursor-pointer"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {loading || !order ? (
                    <div className="flex flex-col items-center py-10">
                        <CommonLoader />
                    </div>
                ) : (
                    <div className="p-4 space-y-4">
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
                            <div>
                                <div className="text-slate-400">{t('purchaseorders.supplier', 'Distributor')}</div>
                                <div className="font-semibold text-gray-700">{order.supplier?.name || '—'}</div>
                                <div className="text-slate-400">{order.supplier?.phone || ''}</div>
                            </div>
                            <div>
                                <div className="text-slate-400">{t('purchaseorders.deliver_to', 'Deliver into')}</div>
                                <div className="font-semibold text-gray-700">{order.store?.name || '—'}</div>
                            </div>
                            <div>
                                <div className="text-slate-400">{t('purchaseorders.expected_date', 'Promised for')}</div>
                                <div className={`font-semibold ${late > 0 ? 'text-rose-600' : 'text-gray-700'}`}>
                                    {fmtDate(order.expectedDate)}
                                    {late > 0 && ` · ${t('purchaseorders.overdue_days', '{{n}} day(s) late', { n: late })}`}
                                </div>
                            </div>
                            <div>
                                <div className="text-slate-400">{t('purchaseorders.status', 'Status')}</div>
                                <span className={`inline-flex px-2 py-0.5 rounded border text-[11px] font-bold capitalize ${STATUS_TONE[order.status] || STATUS_TONE.draft}`}>
                                    {order.status}
                                </span>
                            </div>
                        </div>

                        {order.status === 'cancelled' && order.cancelReason && (
                            <div className="px-3 py-2 rounded-xl bg-rose-50 border border-rose-200 text-xs text-rose-700">
                                {t('purchaseorders.cancelled_because', 'Cancelled: {{reason}}', { reason: order.cancelReason })}
                            </div>
                        )}

                        <div className="overflow-x-auto rounded-xl border border-gray-200">
                            <table className="w-full text-xs">
                                <thead>
                                    <tr className="bg-slate-50 text-slate-500">
                                        <th className="p-2 text-left font-semibold">{t('purchaseorders.product', 'Product')}</th>
                                        <th className="p-2 text-right font-semibold">{t('purchaseorders.ordered', 'Ordered')}</th>
                                        <th className="p-2 text-right font-semibold">{t('purchaseorders.received', 'Received')}</th>
                                        <th className="p-2 text-right font-semibold">{t('purchaseorders.pending', 'Still owed')}</th>
                                        <th className="p-2 text-right font-semibold">{t('purchaseorders.rate_ex_tax', 'Rate (ex tax)')}</th>
                                        <th className="p-2 text-right font-semibold">{t('purchaseorders.gst', 'GST %')}</th>
                                        <th className="p-2 text-right font-semibold">{t('purchaseorders.amount', 'Amount')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((item) => (
                                        <tr key={item.poItemId} className="border-b border-gray-50 last:border-0">
                                            <td className="p-2">
                                                <div className="font-semibold text-gray-700">{item.medicineName}</div>
                                                <div className="text-[10px] text-slate-400">
                                                    {item.packSize ? t('purchaseorders.pack_of', 'pack of {{pack}}', { pack: item.packSize }) : ''}
                                                </div>
                                            </td>
                                            <td className="p-2 text-right">{fmtQty(item.qtyOrdered)}</td>
                                            <td className="p-2 text-right text-emerald-600 font-semibold">{fmtQty(item.qtyReceived)}</td>
                                            <td className={`p-2 text-right font-bold ${toNum(item.qtyPending) > 0 ? 'text-amber-600' : 'text-slate-400'}`}>
                                                {fmtQty(item.qtyPending)}
                                            </td>
                                            <td className="p-2 text-right">{inr(item.rate)}</td>
                                            <td className="p-2 text-right text-slate-500">{toNum(item.gstRate)}%</td>
                                            <td className="p-2 text-right">{inr(item.amount)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="flex justify-end">
                            <div className="w-full sm:w-64 rounded-xl bg-slate-50 border border-slate-200 p-3 text-sm">
                                <div className="flex justify-between text-slate-600">
                                    <span>{t('purchaseorders.sub_total', 'Goods value')}</span>
                                    <span>{inr(order.subTotal)}</span>
                                </div>
                                <div className="flex justify-between text-slate-600 mt-1">
                                    <span>{t('purchaseorders.tax_total', 'GST')}</span>
                                    <span>{inr(order.taxTotal)}</span>
                                </div>
                                <div className="flex justify-between font-bold text-gray-800 mt-2 pt-2 border-t border-slate-200">
                                    <span>{t('purchaseorders.grand_total', 'Order value')}</span>
                                    <span>{inr(order.grandTotal)}</span>
                                </div>
                            </div>
                        </div>

                        {/* WHAT ACTUALLY ARRIVED, read from the receipts rather than
                            inferred from the lines. "How much came" and "in how many
                            deliveries, on what dates, against which supplier invoices"
                            are different questions, and only the second settles an
                            argument with a distributor. */}
                        {receipts.length > 0 && (
                            <div>
                                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                                    {t('purchaseorders.deliveries', 'Deliveries against this order')}
                                </h3>
                                <div className="space-y-1">
                                    {receipts.map((r) => (
                                        <div key={r.grnId} className="flex items-center justify-between text-xs px-3 py-2 rounded-lg bg-slate-50 border border-slate-200">
                                            <span className="font-semibold text-gray-700">{r.grnNo}</span>
                                            <span className="text-slate-500">{fmtDate(r.grnDate)}</span>
                                            <span className="text-slate-500">
                                                {r.supplierInvoiceNo ? t('purchaseorders.their_invoice', 'their bill {{no}}', { no: r.supplierInvoiceNo }) : '—'}
                                            </span>
                                            <span className="font-semibold text-gray-700">{inr(r.grandTotal)}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {order.notes && (
                            <div className="text-xs text-slate-500 px-3 py-2 rounded-xl bg-slate-50 border border-slate-200">
                                {order.notes}
                            </div>
                        )}

                        <div className="flex flex-wrap gap-2 justify-end pt-2 border-t border-gray-100">
                            {order.status === 'draft' && canManage && (
                                <>
                                    <button
                                        type="button"
                                        data-testid="po-detail-edit"
                                        onClick={() => onEdit(order)}
                                        disabled={busy}
                                        className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border border-[#4ABDBD] text-[#2D8A8A] hover:bg-[#4ABDBD]/10 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <FileText className="w-4 h-4" />
                                        {t('purchaseorders.edit', 'Edit')}
                                    </button>
                                    <button
                                        type="button"
                                        data-testid="po-detail-place"
                                        onClick={() => onPlace(order)}
                                        disabled={busy}
                                        className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white shadow-md cursor-pointer hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                        {t('purchaseorders.place', 'Place order')}
                                    </button>
                                </>
                            )}
                            {/* Cancel is drawn only where it can succeed. Once anything
                                has been received the server refuses by name
                                (PO_HAS_RECEIPTS) because a goods receipt points at this
                                order, and a partly-received order that will not be
                                completed is short-closed on the receipt side instead. */}
                            {['draft', 'placed'].includes(order.status) && canManage && (
                                <button
                                    type="button"
                                    data-testid="po-detail-cancel"
                                    onClick={() => onCancelOrder(order)}
                                    disabled={busy}
                                    className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border border-rose-300 text-rose-600 hover:bg-rose-50 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <Ban className="w-4 h-4" />
                                    {t('purchaseorders.cancel_order', 'Cancel order')}
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// The page
// ---------------------------------------------------------------------------

export default function PurchaseOrders() {
    const { t } = useTranslation();
    const { can, canAny } = usePermissions();
    // One memoised instance: it reads the access token inside its request
    // interceptor, so a single copy stays correct across a token refresh.
    const axiosInstance = useMemo(() => createAuthedAxios(), []);

    // Raising and placing orders commits the hospital's money to a distributor and
    // takes the purchasing permission alone — deliberately NOT shared with
    // pharmacy.stock.manage, so a storekeeper who receives goods cannot also order
    // them. Reading is the wider pair, because the storekeeper needs to see what
    // was agreed before accepting a delivery at a different rate.
    const canManage = can('pharmacy.purchase.manage');
    const canRead = canAny(['pharmacy.purchase.manage', 'pharmacy.stock.manage']);

    const [stores, setStores] = useState([]);
    const [storeId, setStoreId] = useState(() => localStorage.getItem(STORE_STORAGE_KEY) || '');

    // 'list' or 'draft'. Kept as a mode rather than a route because the draft is
    // unsaved work: a route change would let the browser's back button discard a
    // basket somebody has spent five minutes on without asking.
    const [view, setView] = useState('list');
    const [draft, setDraft] = useState(null);
    const [saving, setSaving] = useState(false);

    // ---- reorder suggestions ----
    const [cover, setCover] = useState(2);
    const [suggest, setSuggest] = useState(null);
    const [suggestLoading, setSuggestLoading] = useState(true);
    const [expandedGroups, setExpandedGroups] = useState({});
    const [draftingKey, setDraftingKey] = useState(null);

    // ---- the register ----
    const [tab, setTab] = useState('open');
    const [orders, setOrders] = useState([]);
    const [total, setTotal] = useState(0);
    const [listLoading, setListLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const searchTimeoutRef = useRef(null);

    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [mutating, setMutating] = useState(false);

    const [refreshKey, setRefreshKey] = useState(0);
    const refetch = useCallback(() => setRefreshKey((k) => k + 1), []);

    useEffect(() => {
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = setTimeout(() => {
            setDebouncedSearch(searchQuery.trim());
            setCurrentPage(1);
        }, 500);
        return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
    }, [searchQuery]);

    // The counters, once. A hospital running a single counter should never have to
    // choose it, and the last choice is remembered because a buyer works the same
    // counter every morning.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await axiosInstance.get('/pharmacy/stores', { params: { status: 'active' } });
                if (cancelled) return;
                const rows = res?.data?.data || [];
                setStores(rows);
                setStoreId((current) => {
                    const stillThere = rows.some((s) => s.storeId === current);
                    return stillThere
                        ? current
                        : rows.find((s) => s.isDefault)?.storeId || rows[0]?.storeId || '';
                });
            } catch (err) {
                if (!cancelled) toast.warn(apiError(err, t('purchaseorders.stores_error', 'Could not load the pharmacy counters')));
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (storeId) localStorage.setItem(STORE_STORAGE_KEY, storeId);
    }, [storeId]);

    // WHAT TO ORDER NEXT. Deliberately NOT filtered client-side from the register:
    // the netting-off of open orders, the grouping by last supplier and the
    // suggested quantity are all decided by the server against live stock, and a
    // second implementation here would drift from it on the day it mattered.
    //
    // The counter is named rather than summed across the hospital: cover is per
    // counter, and a sum would tell an OP buyer they hold twelve strips when the
    // twelve are on a ward two floors up.
    useEffect(() => {
        if (!canRead) { setSuggestLoading(false); return undefined; }
        let cancelled = false;
        (async () => {
            setSuggestLoading(true);
            try {
                const res = await axiosInstance.get('/pharmacy/purchase-orders/suggest', {
                    params: { storeId: storeId || undefined, cover },
                });
                if (cancelled) return;
                setSuggest(res?.data || null);
            } catch (err) {
                if (cancelled) return;
                setSuggest(null);
                toast.warn(apiError(err, t('purchaseorders.suggest_error', 'Could not build the reorder list')));
            } finally {
                if (!cancelled) setSuggestLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storeId, cover, refreshKey, canRead]);

    // The register.
    useEffect(() => {
        if (!canRead) { setListLoading(false); return undefined; }
        let cancelled = false;
        (async () => {
            setListLoading(true);
            try {
                const tabDef = REGISTER_TABS.find((x) => x.id === tab) || REGISTER_TABS[0];
                const res = await axiosInstance.get('/pharmacy/purchase-orders', {
                    params: {
                        page: currentPage,
                        limit: itemsPerPage,
                        storeId: storeId || undefined,
                        q: debouncedSearch || undefined,
                        ...tabDef.params,
                    },
                });
                if (cancelled) return;
                setOrders(res?.data?.data || []);
                setTotal(res?.data?.total || 0);
            } catch (err) {
                if (cancelled) return;
                setOrders([]);
                setTotal(0);
                toast.warn(apiError(err, t('purchaseorders.list_error', 'Failed to load purchase orders')));
            } finally {
                if (!cancelled) setListLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab, currentPage, itemsPerPage, debouncedSearch, storeId, refreshKey, canRead]);

    // --- turning a suggestion group into an order --------------------------

    const linesFromGroup = (group) =>
        group.items.map((item) => ({
            key: nextLineKey(),
            medicineId: item.medicineId,
            medicineName: item.name,
            packSize: item.packSize || '',
            unit: item.unit || '',
            gstRate: toNum(item.gstRate),
            qtyOrdered: item.suggestedQty,
            // Blank rather than zero when we have never paid for it. A zero looks
            // like a decision; an empty box looks like the question it is.
            rate: item.lastPurchaseRate === null || item.lastPurchaseRate === undefined ? '' : item.lastPurchaseRate,
            hint: {
                qtyOnHand: item.qtyOnHand,
                reorderLevel: item.reorderLevel,
                qtyOnOrder: item.qtyOnOrder,
            },
        }));

    const draftFromOrder = (order) => ({
        poId: order.poId,
        poNo: order.poNo,
        status: order.status,
        storeId: order.storeId,
        supplierId: order.supplierId,
        supplierName: order.supplier?.name || order.supplierName || null,
        paymentTermDays: order.supplier?.paymentTermDays ?? order.paymentTermDays ?? null,
        expectedDate: order.expectedDate ? String(order.expectedDate).slice(0, 10) : '',
        notes: order.notes || '',
        lines: (order.items || []).map((item) => ({
            key: nextLineKey(),
            medicineId: item.medicineId,
            medicineName: item.medicineName,
            packSize: item.packSize || '',
            unit: '',
            gstRate: toNum(item.gstRate),
            qtyOrdered: toNum(item.qtyOrdered),
            rate: toNum(item.rate),
            hint: null,
        })),
    });

    /**
     * ONE CLICK, AND WHAT IT DOES DEPENDS ON WHETHER THE ANSWER IS KNOWN.
     *
     * When the group has a supplier and every line has a rate we have actually
     * paid, the draft is created on the server immediately and then opened for
     * review — a draft commits nothing (it is not sent, it is not counted as
     * on-order by this very screen, and it may be edited freely), so there is no
     * reason to make the buyer press Save to record work the screen already did.
     *
     * When the supplier is unknown or a rate has never been paid, the same button
     * opens the basket UNSAVED with the gaps highlighted instead. Posting it would
     * mean an order at a rate of zero, and the order is the document the
     * supplier's invoice is later checked against — a zero there accepts any price
     * they bill. That is a decision a person has to make, so the click stops in
     * front of them rather than committing a document nobody has read.
     */
    const handleDraftGroup = async (group, needsReview) => {
        const key = group.supplierId || 'unassigned';
        const baseDraft = {
            poId: null,
            poNo: null,
            status: 'draft',
            storeId: suggest?.storeId || storeId,
            supplierId: group.supplierId || null,
            supplierName: group.supplierName || null,
            paymentTermDays: group.paymentTermDays ?? null,
            expectedDate: '',
            notes: '',
            lines: linesFromGroup(group),
        };

        if (needsReview) {
            setDraft(baseDraft);
            setView('draft');
            toast.info(
                group.supplierId
                    ? t('purchaseorders.needs_rates', 'Some of these have never been bought — key the rates before saving')
                    : t('purchaseorders.needs_supplier', 'Choose a distributor for these — they have never been received from anybody')
            );
            return;
        }

        setDraftingKey(key);
        try {
            const res = await axiosInstance.post('/pharmacy/purchase-orders', {
                storeId: baseDraft.storeId,
                supplierId: baseDraft.supplierId,
                items: baseDraft.lines.map((l) => ({
                    medicineId: l.medicineId,
                    qtyOrdered: toNum(l.qtyOrdered),
                    rate: toNum(l.rate),
                    gstRate: toNum(l.gstRate),
                    packSize: l.packSize || null,
                })),
            });
            const created = res?.data?.data;
            toast.success(t('purchaseorders.draft_created', 'Draft order created — check the rates, then place it'));
            setDraft(created ? { ...draftFromOrder(created), supplierName: group.supplierName, paymentTermDays: group.paymentTermDays } : baseDraft);
            setView('draft');
            refetch();
        } catch (err) {
            toast.warn(apiError(err, t('purchaseorders.draft_error', 'Could not create the draft order')));
        } finally {
            setDraftingKey(null);
        }
    };

    const startBlankOrder = () => {
        setDraft({
            poId: null,
            poNo: null,
            status: 'draft',
            storeId: storeId,
            supplierId: null,
            supplierName: null,
            paymentTermDays: null,
            expectedDate: '',
            notes: '',
            lines: [],
        });
        setView('draft');
    };

    // --- saving and placing -------------------------------------------------

    const saveDraft = async ({ place }) => {
        if (!draft) return;
        const payload = {
            storeId: draft.storeId,
            supplierId: draft.supplierId,
            expectedDate: draft.expectedDate || null,
            notes: draft.notes || null,
            items: draft.lines.map((l) => ({
                medicineId: l.medicineId,
                qtyOrdered: toNum(l.qtyOrdered),
                rate: toNum(l.rate),
                gstRate: toNum(l.gstRate),
                packSize: l.packSize || null,
            })),
        };

        setSaving(true);
        try {
            let poId = draft.poId;
            if (poId) {
                await axiosInstance.put(`/pharmacy/purchase-orders/${poId}`, payload);
            } else {
                const res = await axiosInstance.post('/pharmacy/purchase-orders', payload);
                poId = res?.data?.data?.poId;
                // The saved id is kept even when the placement below fails, so a
                // retry updates the draft that now exists rather than creating a
                // second one and leaving an orphan in the register.
                setDraft((d) => (d ? { ...d, poId } : d));
            }

            if (!place) {
                toast.success(t('purchaseorders.draft_saved', 'Draft saved'));
                setView('list');
                setDraft(null);
                refetch();
                return;
            }

            const placed = await axiosInstance.post(`/pharmacy/purchase-orders/${poId}/place`, {});
            toast.success(placed?.data?.message || t('purchaseorders.placed', 'Order placed'));
            setView('list');
            setDraft(null);
            // Both panels refresh: a placed order is netted off every shortfall it
            // covers, so the reorder list the buyer is looking at is out of date the
            // instant this succeeds — and a list that still shows a product they
            // just ordered is how it gets ordered twice.
            refetch();
        } catch (err) {
            const code = errorCode(err);
            // The server's own words, not a house string. SUPPLIER_INACTIVE and
            // PO_ALREADY_PLACED each name the thing to do next; "Failed to save"
            // names nothing.
            toast.warn(apiError(err, t('purchaseorders.save_error', 'Could not save the order')));
            if (code === 'PO_ALREADY_PLACED') { setView('list'); setDraft(null); refetch(); }
        } finally {
            setSaving(false);
        }
    };

    // --- register actions ---------------------------------------------------

    const openDetail = async (order) => {
        setDetail({ poId: order.poId });
        setDetailLoading(true);
        try {
            const res = await axiosInstance.get(`/pharmacy/purchase-orders/${order.poId}`);
            setDetail(res?.data?.data || null);
        } catch (err) {
            setDetail(null);
            toast.warn(apiError(err, t('purchaseorders.detail_error', 'Could not open the order')));
        } finally {
            setDetailLoading(false);
        }
    };

    const handlePlace = async (order) => {
        const result = await MySwal.fire({
            title: t('purchaseorders.place_title', 'Send this order?'),
            text: t('purchaseorders.place_text', 'It takes the next order number and the lines freeze. Changing it afterwards means cancelling and raising another.'),
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#e5e7eb',
            confirmButtonText: t('purchaseorders.place', 'Place order'),
            cancelButtonText: t('purchaseorders.cancel', 'Cancel'),
        });
        if (!result.isConfirmed) return;

        setMutating(true);
        try {
            const res = await axiosInstance.post(`/pharmacy/purchase-orders/${order.poId}/place`, {});
            toast.success(res?.data?.message || t('purchaseorders.placed', 'Order placed'));
            setDetail(null);
            refetch();
        } catch (err) {
            toast.warn(apiError(err, t('purchaseorders.place_error', 'Could not place the order')));
            // Whatever the refusal, this screen's copy of the status is now suspect
            // — refetching is cheaper than reasoning about which of the five states
            // the row is really in.
            refetch();
        } finally {
            setMutating(false);
        }
    };

    /**
     * Abandon an order. THE REASON IS MANDATORY and the server refuses without one
     * — a cancelled order with no reason is indistinguishable from one cancelled to
     * bury a rate somebody should not have agreed, and the supplier will ask,
     * because they may already have picked the stock.
     */
    const handleCancelOrder = async (order) => {
        const result = await MySwal.fire({
            title: t('purchaseorders.cancel_title', 'Cancel this order?'),
            input: 'text',
            inputLabel: t('purchaseorders.cancel_reason_label', 'Why? The distributor may already have picked it.'),
            inputPlaceholder: t('purchaseorders.cancel_reason_placeholder', 'Ordered in error, rate not honoured, no longer needed…'),
            inputValidator: (value) =>
                (value && value.trim().length >= 3)
                    ? undefined
                    : t('purchaseorders.cancel_reason_required', 'A reason is required'),
            showCancelButton: true,
            confirmButtonColor: '#e11d48',
            cancelButtonColor: '#e5e7eb',
            confirmButtonText: t('purchaseorders.cancel_order', 'Cancel order'),
            cancelButtonText: t('purchaseorders.keep', 'Keep it'),
        });
        if (!result.isConfirmed) return;

        setMutating(true);
        try {
            await axiosInstance.post(`/pharmacy/purchase-orders/${order.poId}/cancel`, {
                reason: String(result.value || '').trim(),
            });
            toast.success(t('purchaseorders.cancelled', 'Purchase order cancelled'));
            setDetail(null);
            refetch();
        } catch (err) {
            toast.warn(apiError(err, t('purchaseorders.cancel_error', 'Could not cancel the order')));
            refetch();
        } finally {
            setMutating(false);
        }
    };

    const handleEditDraft = (order) => {
        setDraft(draftFromOrder(order));
        setDetail(null);
        setView('draft');
    };

    const handlePageChange = (page, newItemsPerPage) => {
        if (newItemsPerPage && newItemsPerPage !== itemsPerPage) {
            setItemsPerPage(newItemsPerPage);
            setCurrentPage(1);
        } else {
            setCurrentPage(page);
        }
    };

    // --- render -------------------------------------------------------------

    if (!canRead) {
        return (
            <div data-testid="po-no-access" className="bg-white p-6 rounded-2xl shadow-md w-full text-center text-sm text-slate-500">
                {t('purchaseorders.no_read_permission', 'Purchase rates are the hospital’s trade position, so this register is kept to the buyer and the storekeeper. Ask an administrator for the purchasing or stock permission.')}
            </div>
        );
    }

    if (view === 'draft' && draft) {
        return (
            <div data-testid="purchase-orders-page-container" className="bg-white p-4 rounded-2xl shadow-md sm:p-6 w-full">
                <DraftEditor
                    axiosInstance={axiosInstance}
                    draft={draft}
                    setDraft={setDraft}
                    stores={stores}
                    canManage={canManage}
                    saving={saving}
                    onSave={saveDraft}
                    onCancel={() => { setView('list'); setDraft(null); refetch(); }}
                    t={t}
                />
            </div>
        );
    }

    const groups = suggest?.data || [];
    const selectedStore = stores.find((s) => s.storeId === storeId) || null;

    return (
        <div data-testid="purchase-orders-page-container" className="bg-white p-4 rounded-2xl shadow-md sm:p-6 w-full">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                <div className="ml-2">
                    <h1 data-testid="purchase-orders-page-title" className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
                        {t('purchaseorders.title', 'Purchase orders')}
                    </h1>
                    <p className="text-xs text-slate-400 mt-1">
                        {t('purchaseorders.subtitle', 'What to order next, what has been asked for, and what the distributors still owe.')}
                    </p>
                </div>
                <button
                    data-testid="po-new-order"
                    onClick={startBlankOrder}
                    disabled={!canManage || stores.length === 0}
                    title={canManage ? undefined : t('purchaseorders.no_permission', 'Raising orders needs the purchasing permission')}
                    className={`group flex items-center gap-2 w-full sm:w-auto px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg transition-all duration-300 font-semibold text-sm whitespace-nowrap ${!canManage || stores.length === 0 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:shadow-xl hover:scale-105'}`}
                >
                    <Plus className="w-5 h-5" />
                    {t('purchaseorders.new_order', 'New order')}
                </button>
            </div>

            {/* Which counter these figures belong to, in words. Stock is held per
                counter and so is cover, so a reorder list without a named counter is
                a list somebody acts on for the wrong room. */}
            <div className="mb-6 flex flex-col sm:flex-row sm:items-center gap-2 px-3 py-2 rounded-xl bg-slate-50 border border-slate-200">
                <div className="flex items-center gap-2 text-xs text-slate-500">
                    <StoreIcon className="w-4 h-4 text-[#4ABDBD]" />
                    {t('purchaseorders.buying_for', 'Buying for')}
                </div>
                <select
                    data-testid="po-store-picker"
                    value={storeId}
                    onChange={(e) => { setStoreId(e.target.value); setCurrentPage(1); }}
                    className="tt-select p-1.5 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                >
                    {stores.length === 0 && <option value="">{t('purchaseorders.no_stores', 'No counter set up yet')}</option>}
                    {stores.map((s) => (
                        <option key={s.storeId} value={s.storeId}>
                            {s.name}{s.isDefault ? ` · ${t('purchaseorders.default_store', 'default')}` : ''}
                        </option>
                    ))}
                </select>
                <span className="text-[11px] text-slate-400 sm:ml-auto">
                    {selectedStore
                        ? t('purchaseorders.cover_is_per_counter', 'Cover is counted for this counter only.')
                        : t('purchaseorders.set_up_a_counter', 'Set up a pharmacy counter before ordering.')}
                </span>
            </div>

            {/* ============================ THE REORDER LIST ==================== */}

            <section data-testid="po-suggest-panel" className="mb-8">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
                    <div>
                        <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                            <PackageX className="w-5 h-5 text-[#2D8A8A]" />
                            {t('purchaseorders.suggest_title', 'What to order next')}
                        </h2>
                        <p className="text-xs text-slate-400">
                            {t('purchaseorders.suggest_subtitle', 'Everything at or below its reorder level, already netted against what is on its way, grouped by the distributor it was last received from.')}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <label className="text-[11px] text-slate-500 whitespace-nowrap">
                            {t('purchaseorders.refill_to', 'Refill to')}
                        </label>
                        <select
                            data-testid="po-cover-picker"
                            value={cover}
                            onChange={(e) => setCover(Number(e.target.value))}
                            className="tt-select p-1.5 border border-gray-300 rounded-lg text-xs bg-white focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                        >
                            {COVER_OPTIONS.map((c) => (
                                <option key={c} value={c}>
                                    {t('purchaseorders.cover_option', '{{n}}× the reorder level', { n: c })}
                                </option>
                            ))}
                        </select>
                        <button
                            type="button"
                            data-testid="po-suggest-refresh"
                            onClick={refetch}
                            disabled={suggestLoading}
                            title={t('purchaseorders.refresh', 'Refresh')}
                            className="p-2 rounded-lg border border-gray-300 text-slate-500 hover:text-[#2D8A8A] hover:border-[#4ABDBD] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <RefreshCw className={`w-4 h-4 ${suggestLoading ? 'animate-spin' : ''}`} />
                        </button>
                    </div>
                </div>

                {/* A level is a FLOOR, not a target — refilling back to it means the
                    next sale drops below it again and the buyer raises the same order
                    tomorrow. Saying so next to the multiplier is what stops somebody
                    "correcting" it to 1×. */}
                <p className="text-[11px] text-slate-400 mb-3">
                    {t('purchaseorders.cover_hint', 'The reorder level is the buffer that must survive while the next delivery is in transit, so the suggestion refills past it rather than back up to it.')}
                </p>

                {suggestLoading ? (
                    <div className="flex flex-col items-center justify-center py-8">
                        <CommonLoader />
                    </div>
                ) : (
                    <>
                        {suggest && (
                            <div className="flex flex-wrap gap-2 mb-3 text-[11px]">
                                <span className="px-2 py-1 rounded-lg bg-[#4ABDBD]/10 text-[#2D8A8A] border border-[#4ABDBD]/40 font-semibold">
                                    {t('purchaseorders.summary_items', '{{n}} product(s) to order', { n: suggest.totalItems || 0 })}
                                </span>
                                <span className="px-2 py-1 rounded-lg bg-slate-100 text-slate-600 border border-slate-200 font-semibold">
                                    {t('purchaseorders.summary_groups', 'across {{n}} distributor(s)', { n: groups.length })}
                                </span>
                                {/* Reported, not hidden. A buyer who knows a product is
                                    short and cannot see it on this list will assume the
                                    list is broken and go back to ordering by memory. */}
                                {suggest.coveredByOpenOrders > 0 && (
                                    <span className="px-2 py-1 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold">
                                        <CheckCircle2 className="w-3 h-3 inline mr-1" />
                                        {t('purchaseorders.summary_covered', '{{n}} short but already on order — not repeated', { n: suggest.coveredByOpenOrders })}
                                    </span>
                                )}
                            </div>
                        )}

                        {suggest?.truncated && (
                            <div data-testid="po-suggest-truncated" className="mb-3 px-3 py-2 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-800">
                                {t('purchaseorders.suggest_truncated', 'More products are below their level than one screen can carry. Order what is listed, then refresh — a truncated list that looks complete is how the last twenty are never ordered.')}
                            </div>
                        )}

                        {groups.length === 0 ? (
                            <div data-testid="po-suggest-empty" className="px-4 py-8 rounded-2xl border border-dashed border-gray-300 text-center">
                                <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                                <p className="text-sm font-semibold text-gray-700">
                                    {t('purchaseorders.suggest_empty', 'Nothing is below its reorder level at this counter.')}
                                </p>
                                {/* The quiet failure mode of this screen: a product with
                                    no level set is never suggested, however empty the
                                    shelf is. Saying it here is the only place a buyer
                                    would find out. */}
                                <p className="text-xs text-slate-400 mt-1 max-w-lg mx-auto">
                                    {t('purchaseorders.suggest_empty_hint', 'Only products with a reorder level set are watched — a level of zero means “not tracked”, not “reorder at zero”. Set levels in the medicine master for anything that should appear here.')}
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {groups.map((group) => {
                                    const key = group.supplierId || 'unassigned';
                                    return (
                                        <SuggestionGroup
                                            key={key}
                                            group={group}
                                            expanded={!!expandedGroups[key]}
                                            onToggle={() => setExpandedGroups((prev) => ({ ...prev, [key]: !prev[key] }))}
                                            onDraft={handleDraftGroup}
                                            canManage={canManage}
                                            busy={draftingKey === key}
                                            t={t}
                                        />
                                    );
                                })}
                            </div>
                        )}
                    </>
                )}
            </section>

            {/* ============================ THE REGISTER ======================== */}

            <section data-testid="po-register">
                <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-3">
                    <Truck className="w-5 h-5 text-[#2D8A8A]" />
                    {t('purchaseorders.register_title', 'Orders raised')}
                </h2>

                <div className="mb-4 flex flex-col sm:flex-row gap-2 sm:items-center flex-wrap">
                    <div className="relative flex bg-gradient-to-r from-gray-50 to-blue-50/30 p-1 rounded-2xl shadow border border-gray-200 gap-1 flex-wrap">
                        {REGISTER_TABS.map((x) => (
                            <button
                                key={x.id}
                                data-testid={`po-tab-${x.id}`}
                                onClick={() => { setTab(x.id); setCurrentPage(1); }}
                                className={`px-4 py-2 text-xs sm:text-sm rounded-xl transition-colors duration-300 ${tab === x.id
                                    ? 'bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white shadow-lg'
                                    : 'text-gray-600 hover:text-[#4ABDBD] cursor-pointer'}`}
                            >
                                {t(x.labelKey, x.label)}
                            </button>
                        ))}
                    </div>

                    <div className="relative w-full sm:w-72">
                        <input
                            data-testid="po-search-input"
                            type="text"
                            placeholder={t('purchaseorders.search_placeholder', 'Search by order number…')}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value.replace(/[<>]/g, '').slice(0, 40))}
                            maxLength={40}
                            className="w-full p-2 pl-10 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                        />
                        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4ABDBD]" />
                    </div>
                </div>

                {listLoading ? (
                    <div className="flex flex-col items-center justify-center py-4 pt-12">
                        <CommonLoader />
                        <span className="text-xl text-[#4ABDBD]">{t('purchaseorders.loading', 'Loading...')}</span>
                    </div>
                ) : (
                    <>
                        <div className="hidden lg:block overflow-x-auto rounded-md border border-gray-200">
                            <table data-testid="po-table" className="w-full border-collapse rounded-md overflow-hidden">
                                <thead>
                                    <tr className="bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white">
                                        <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('purchaseorders.order_no', 'Order')}</th>
                                        <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('purchaseorders.supplier', 'Distributor')}</th>
                                        <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('purchaseorders.raised', 'Raised')}</th>
                                        <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('purchaseorders.expected_date', 'Promised for')}</th>
                                        <th className="p-3 text-right text-xs font-bold uppercase tracking-wider">{t('purchaseorders.lines_short', 'Lines')}</th>
                                        <th className="p-3 text-right text-xs font-bold uppercase tracking-wider">{t('purchaseorders.pending', 'Still owed')}</th>
                                        <th className="p-3 text-right text-xs font-bold uppercase tracking-wider">{t('purchaseorders.value', 'Value')}</th>
                                        <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('purchaseorders.status', 'Status')}</th>
                                        <th className="p-3 text-center text-xs font-bold uppercase tracking-wider">{t('purchaseorders.action', 'Action')}</th>
                                    </tr>
                                </thead>
                                <tbody data-testid="po-table-body">
                                    {orders.length > 0 ? orders.map((o) => {
                                        const late = ['placed', 'partial'].includes(o.status) ? daysLate(o.expectedDate) : 0;
                                        return (
                                            <tr
                                                key={o.poId}
                                                data-testid={`po-row-${o.poId}`}
                                                className="border-b border-gray-100/50 hover:bg-gradient-to-r hover:from-blue-50/80 hover:to-cyan-50/80 transition-all duration-300"
                                            >
                                                <td className="p-3 text-xs">
                                                    {/* A draft has no number. Printing the internal
                                                        placeholder would give somebody something to
                                                        read down a telephone that no supplier could
                                                        match. */}
                                                    <span className={`font-mono font-semibold ${o.poNo ? 'text-gray-700' : 'text-slate-400 italic'}`}>
                                                        {o.poNo || t('purchaseorders.not_yet_placed', 'not yet placed')}
                                                    </span>
                                                </td>
                                                <td className="p-3 text-xs">
                                                    <div className="font-semibold text-gray-700">{o.supplierName || '—'}</div>
                                                    {o.paymentTermDays !== null && o.paymentTermDays !== undefined && (
                                                        <div className="text-[11px] text-slate-400">
                                                            {t('purchaseorders.terms_days', '{{days}} day terms', { days: o.paymentTermDays })}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="p-3 text-xs text-gray-600">{fmtDate(o.poDate)}</td>
                                                <td className="p-3 text-xs">
                                                    <span className={late > 0 ? 'text-rose-600 font-semibold' : 'text-gray-600'}>
                                                        {fmtDate(o.expectedDate)}
                                                    </span>
                                                    {late > 0 && (
                                                        <div className="inline-flex items-center gap-1 mt-0.5 px-1.5 py-0.5 rounded bg-rose-50 text-rose-600 border border-rose-200 text-[10px] font-bold">
                                                            <Clock className="w-3 h-3" />
                                                            {t('purchaseorders.overdue_days', '{{n}} day(s) late', { n: late })}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="p-3 text-xs text-right text-gray-600">{o.lineCount ?? '—'}</td>
                                                <td className={`p-3 text-xs text-right font-bold ${toNum(o.qtyPending) > 0 ? 'text-amber-600' : 'text-slate-400'}`}>
                                                    {fmtQty(o.qtyPending)}
                                                </td>
                                                <td className="p-3 text-xs text-right text-gray-700">{inr(o.grandTotal)}</td>
                                                <td className="p-3 text-xs">
                                                    <span className={`inline-flex px-2 py-0.5 rounded border text-[11px] font-bold capitalize ${STATUS_TONE[o.status] || STATUS_TONE.draft}`}>
                                                        {o.status}
                                                    </span>
                                                </td>
                                                <td className="p-3 text-xs">
                                                    <div className="flex justify-center gap-2">
                                                        <button
                                                            data-testid={`po-open-${o.poId}`}
                                                            onClick={() => openDetail(o)}
                                                            title={t('purchaseorders.open', 'Open')}
                                                            className="text-[#4ABDBD] p-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 shadow-md transition-all duration-300 hover:text-white hover:from-[#4ABDBD] hover:to-[#3AA8A8] cursor-pointer hover:scale-110"
                                                        >
                                                            <Eye className="h-4 w-4" />
                                                        </button>
                                                        {o.status === 'draft' && canManage && (
                                                            <button
                                                                data-testid={`po-place-${o.poId}`}
                                                                onClick={() => handlePlace(o)}
                                                                disabled={mutating}
                                                                title={t('purchaseorders.place', 'Place order')}
                                                                className={`text-emerald-600 p-2 rounded-xl bg-gradient-to-r from-emerald-50 to-teal-50 shadow-md transition-all duration-300 ${mutating ? 'opacity-40 cursor-not-allowed' : 'hover:text-white hover:from-emerald-500 hover:to-teal-500 cursor-pointer hover:scale-110'}`}
                                                            >
                                                                <Send className="h-4 w-4" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    }) : (
                                        <tr>
                                            <td colSpan={9} className="p-6 text-center text-gray-500 text-sm">
                                                {tab === 'open'
                                                    ? t('purchaseorders.none_open', 'Nothing is outstanding with a distributor.')
                                                    : t('purchaseorders.none_here', 'No orders here.')}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Cards below lg — nine columns do not survive a narrow screen,
                            and a purchase register is read on a tablet in a stock room. */}
                        <div className="block lg:hidden space-y-3">
                            {orders.length > 0 ? orders.map((o) => {
                                const late = ['placed', 'partial'].includes(o.status) ? daysLate(o.expectedDate) : 0;
                                return (
                                    <div key={o.poId} data-testid={`po-card-${o.poId}`} className="p-4 bg-gray-50 rounded-xl shadow-md">
                                        <div className="flex justify-between items-start gap-2">
                                            <div>
                                                <div className={`font-mono text-sm font-semibold ${o.poNo ? 'text-gray-800' : 'text-slate-400 italic'}`}>
                                                    {o.poNo || t('purchaseorders.not_yet_placed', 'not yet placed')}
                                                </div>
                                                <div className="text-xs text-gray-600">{o.supplierName || '—'}</div>
                                                <div className="text-[11px] text-slate-400">{fmtDate(o.poDate)}</div>
                                            </div>
                                            <span className={`inline-flex px-2 py-0.5 rounded border text-[11px] font-bold capitalize ${STATUS_TONE[o.status] || STATUS_TONE.draft}`}>
                                                {o.status}
                                            </span>
                                        </div>
                                        <div className="flex flex-wrap gap-2 mt-3 text-[11px]">
                                            <span className="px-2 py-0.5 rounded bg-white border border-gray-200 text-gray-600">
                                                {t('purchaseorders.lines_n', '{{n}} line(s)', { n: o.lineCount ?? 0 })}
                                            </span>
                                            <span className="px-2 py-0.5 rounded bg-white border border-gray-200 text-gray-600">
                                                {inr(o.grandTotal)}
                                            </span>
                                            {toNum(o.qtyPending) > 0 && (
                                                <span className="px-2 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-700 font-semibold">
                                                    {t('purchaseorders.pending_n', '{{n}} still owed', { n: fmtQty(o.qtyPending) })}
                                                </span>
                                            )}
                                            {late > 0 && (
                                                <span className="px-2 py-0.5 rounded bg-rose-50 border border-rose-200 text-rose-600 font-semibold">
                                                    {t('purchaseorders.overdue_days', '{{n}} day(s) late', { n: late })}
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex gap-2 mt-3">
                                            <button
                                                data-testid={`po-open-card-${o.poId}`}
                                                onClick={() => openDetail(o)}
                                                className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg border border-[#4ABDBD] text-[#2D8A8A] text-xs font-semibold cursor-pointer"
                                            >
                                                <Eye className="w-3.5 h-3.5" />
                                                {t('purchaseorders.open', 'Open')}
                                            </button>
                                            {o.status === 'draft' && canManage && (
                                                <button
                                                    data-testid={`po-place-card-${o.poId}`}
                                                    onClick={() => handlePlace(o)}
                                                    disabled={mutating}
                                                    className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A] text-white text-xs font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    <Send className="w-3.5 h-3.5" />
                                                    {t('purchaseorders.place', 'Place order')}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            }) : (
                                <div className="p-6 text-center text-gray-500 text-sm">
                                    {t('purchaseorders.none_here', 'No orders here.')}
                                </div>
                            )}
                        </div>
                    </>
                )}

                {!listLoading && total > 0 && (
                    <Pagination
                        totalItems={total}
                        currentPage={currentPage}
                        itemsPerPage={itemsPerPage}
                        onPageChange={handlePageChange}
                    />
                )}
            </section>

            {detail && (
                <OrderDetail
                    // While the fetch is in flight `detail` is only the id we
                    // clicked, so the panel is handed nothing and draws its loader
                    // rather than a half-populated order with blank totals.
                    order={detailLoading ? null : detail}
                    loading={detailLoading}
                    onClose={() => setDetail(null)}
                    onPlace={handlePlace}
                    onCancelOrder={handleCancelOrder}
                    onEdit={handleEditDraft}
                    canManage={canManage}
                    busy={mutating}
                    t={t}
                />
            )}
        </div>
    );
}
